import React, { useState, useContext, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { PageTitle, PageSubtitle, TooltipHintWrapper, PageSubtitleWrapper } from './style'
import { MonitoringContext } from 'providers/monitoring'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import MonitoringAboutMultiSelect from 'flow/SiteMonitoring/components/MonitoringAboutMultiSelect'
import Select from 'components/Select/selectAbout'
import Textarea from 'components/Textarea'
import UploadSiteFiles from 'components/SitesUploads'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import Uploader from './uploader'
import { monitoringQtySpr, getAboutMonitoring } from 'api/monitoring'
import ScrollToTop from 'components/ScrollToTop'
import Loader from 'components/Loader'
import LoadingWrapper from 'components/LoadingWrapper'

const tooltipXOffset = 0
const tooltipYOffset = 16
const inputSaveDelayTime = 300
const evaluationTypeOptions = [
  { label: 'State', value: 1 },
  { label: 'Pressure', value: 2 },
  { label: 'Response', value: 3 }
]
const MAX_TEXTBOX_CHAR_LIMIT = 255

const MonitoringAbout = () => {
  const params = useParams()
  const [stateDataQuality, setStateDataQuality] = useState('')
  const [pressureDataQuality, setPressureDataQuality] = useState('')
  const [responseDataQuality, setResponseDataQuality] = useState('')
  const prevCheckedRef = React.useRef([])
  const [additionalFeedback, setAdditionalFeedback] = useState('')
  const [provideAnyReferences, setProvideAnyReferences] = useState('')
  const [anyOtherDetailsRelevance, setAnyOtherDetailsRelevance] = useState('')
  const saveTimeoutRef = useRef(null)
  const { updateMonitoring, monitoringProposalFieldValidation, isEditable } = useContext(MonitoringContext)
  const [evaluationType, setEvaluationType] = useState([])
  const [aboutLoading, setAboutLoading] = useState(true)
  const [appliedEvaluationType, setAppliedEvaluationType] = useState([])
  const [monitoringSPR, setMonitoringSPR] = useState([])
  const [monitoringFiles, setMonitoringFiles] = useState([])
  const mergedParams = params

  const handleValueChecked = (checkedValues) => {
    const selected = checkedValues.filter((c) => c?.checked).map((c) => c.value)
    const prev = prevCheckedRef.current || []
    const same = prev.length === selected.length && prev.every((v, i) => v === selected[i])
    if (!same) {
      prevCheckedRef.current = selected
      handleEvaluationTypeChange(selected)
    }
  }
  const handleEvaluationTypeChange = (e) => {
    setEvaluationType(e)
  }
  const scheduleSave = (field, value) => {
    if (!updateMonitoring) return
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    saveTimeoutRef.current = setTimeout(() => {
      if (field === 'evaluationType') {
        const values = Array.isArray(value) ? value : []
        const payload = {
          state_evaluation: values.includes(1),
          pressure_evaluation: values.includes(2),
          response_evaluation: values.includes(3)
        }
        updateMonitoring(payload)
      } else {
        updateMonitoring({ [field]: value })
      }
    }, inputSaveDelayTime)
  }
  const handleStateDataQualityChange = async (selectedPurpose) => {
    setStateDataQuality(selectedPurpose)
    scheduleSave('state_quality_id', selectedPurpose.value)
  }
  const handlePressureDataQualityChange = async (selectedPurpose) => {
    setPressureDataQuality(selectedPurpose)
    scheduleSave('pressure_quality_id', selectedPurpose.value)
  }
  const handleResponseDataQualityChange = async (selectedPurpose) => {
    setResponseDataQuality(selectedPurpose)
    scheduleSave('response_quality_id', selectedPurpose.value)
  }
  const onAdditionalFeedbackChange = async (e) => {
    const val = e.target.value || ''
    const truncated = val.slice(0, MAX_TEXTBOX_CHAR_LIMIT)
    setAdditionalFeedback(truncated)
    scheduleSave('quality_site_knowledge', truncated)
  }
  const onProvideAnyReferencesChange = async (e) => {
    const val = e.target.value || ''
    const truncated = val.slice(0, MAX_TEXTBOX_CHAR_LIMIT)
    setProvideAnyReferences(truncated)
    scheduleSave('quality_site_source', truncated)
  }
  const onAnyOtherDetailsRelevanceChange = async (e) => {
    const val = e.target.value || ''
    const truncated = val.slice(0, MAX_TEXTBOX_CHAR_LIMIT)
    setAnyOtherDetailsRelevance(truncated)
    scheduleSave('quality_additional_details', truncated)
  }

  const monitoringFetchedRef = useRef(false)
  useEffect(() => {
    // guard to avoid double-call in React StrictMode (dev) or repeated mounts
    if (monitoringFetchedRef.current) return
    monitoringFetchedRef.current = true
    let mounted = true
    monitoringQtySpr()
      .then((data) => {
        if (!Array.isArray(data)) {
          if (mounted) setMonitoringSPR([])
          return
        }
        const mapped = data.map(({ id, name, ...rest }) => ({
          value: id,
          label: name
        }))
        if (mounted) setMonitoringSPR(mapped)
        // after we have options, fetch existing monitoring about data and populate fields
        getAboutMonitoring(params.monitoringId)
          .then((res) => {
            if (!res || typeof res !== 'object') return
            // evaluationType mapping
            const et = []
            if (res?.state_evaluation === true) et.push(1)
            if (res?.pressure_evaluation === true) et.push(2)
            if (res?.response_evaluation === true) et.push(3)
            if (mounted) {
              setEvaluationType(et)
              // if server already had evaluation types enabled, treat them as applied
              setAppliedEvaluationType(et)
              prevCheckedRef.current = et
            }
            if (mounted) {
              const findOption = (id) => mapped.find((o) => String(o.value) === String(id)) || ''
              setStateDataQuality(res?.state_quality_id ? findOption(res.state_quality_id) : '')
              setPressureDataQuality(res?.pressure_quality_id ? findOption(res.pressure_quality_id) : '')
              setResponseDataQuality(res?.response_quality_id ? findOption(res.response_quality_id) : '')
              setAdditionalFeedback(res?.quality_site_knowledge ?? res.monitoringComments ?? '')
              setProvideAnyReferences(res?.quality_site_source ?? '')
              setAnyOtherDetailsRelevance(res?.quality_additional_details ?? '')
              setMonitoringFiles(res?.monitoringFiles ?? [])
            }
            if (mounted) {
              setAboutLoading(false)
            }
          })
          .catch(() => {
            if (mounted) setAboutLoading(false)
          })
      })
      .catch(() => {
        if (mounted) setAboutLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [])
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [])

  return (
    <div>
      <PageTitle data-test="legend">
        <FormattedMessage id="monitoringAboutPageTitle" />
      </PageTitle>
      <PageSubtitleWrapper>
        <PageSubtitle data-test="sub-legend">
          <FormattedMessage id="monitoringAboutPageSubTitle" />
        </PageSubtitle>
        <Tooltip
          style={{ zIndex: 1000 }}
          placement="bottom"
          trigger={['hover']}
          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
          overlay={{ label: 'monitoringAboutPageTitle', infoTooltip: 'monitoringAboutProposalHint' }}
          type="big"
        >
          <TooltipHintWrapper>
            <InfoIcon />
          </TooltipHintWrapper>
        </Tooltip>
      </PageSubtitleWrapper>
      {aboutLoading ? (
        <LoadingWrapper>
          <Loader />
        </LoadingWrapper>
      ) : (
        <Container>
          <Row>
            <Col md={5} mdOffset={2}>
              <MonitoringAboutMultiSelect
                marginTop="16px"
                label="evaluationTypeLabel"
                field="evaluationType"
                infoTooltip="statePressureQualityOptions"
                secondStepOptions={evaluationTypeOptions}
                value={evaluationType}
                saveChanges={(vals) => {
                  handleEvaluationTypeChange(vals)
                  const applied = Array.isArray(vals) ? vals : []
                  setAppliedEvaluationType(applied)
                  prevCheckedRef.current = applied
                  scheduleSave('evaluationType', vals)
                }}
                valueChecked={handleValueChecked}
                disabled={!isEditable}
                selectAllOption={true}
                error={
                  !monitoringProposalFieldValidation?.about?.state_evaluation &&
                  !monitoringProposalFieldValidation?.about?.pressure_evaluation &&
                  !monitoringProposalFieldValidation?.about?.response_evaluation
                    ? {
                        field: 'evaluationType',
                        error: 'pleaseCompleteThisField'
                      }
                    : { field: '', error: '' }
                }
              />
            </Col>
          </Row>
          {evaluationType.includes(1) && (
            <Row>
              <Col md={8} mdOffset={2}>
                <Select
                  value={stateDataQuality}
                  onChange={handleStateDataQualityChange}
                  placeholder="stateDataQualityPlaceholder"
                  label="stateDataQualityLabel"
                  field="stateDataQuality"
                  infoTooltip="stateDataQualityHint"
                  options={monitoringSPR}
                  disabled={!appliedEvaluationType.includes(1) || !isEditable}
                  blueBg={true}
                  error={
                    !monitoringProposalFieldValidation?.about?.state_quality_id && evaluationType.includes(1)
                      ? {
                          field: 'stateDataQuality',
                          error: 'pleaseCompleteThisField'
                        }
                      : { field: '', error: '' }
                  }
                />
              </Col>
            </Row>
          )}
          {evaluationType.includes(2) && (
            <Row>
              <Col md={8} mdOffset={2}>
                <Select
                  value={pressureDataQuality}
                  onChange={handlePressureDataQualityChange}
                  placeholder="pressureDataQualityPlaceholder"
                  label="pressureDataQualityLabel"
                  field="pressureDataQuality"
                  infoTooltip="pressureDataQualityHint"
                  options={monitoringSPR}
                  disabled={!appliedEvaluationType.includes(2) || !isEditable}
                  blueBg={true}
                  error={
                    !monitoringProposalFieldValidation?.about?.pressure_quality_id && evaluationType.includes(2)
                      ? {
                          field: 'pressureDataQuality',
                          error: 'pleaseCompleteThisField'
                        }
                      : { field: '', error: '' }
                  }
                />
              </Col>
            </Row>
          )}
          {evaluationType.includes(3) && (
            <Row>
              <Col md={8} mdOffset={2}>
                <Select
                  value={responseDataQuality}
                  onChange={handleResponseDataQualityChange}
                  placeholder="responseDataQualityPlaceholder"
                  label="responseDataQualityLabel"
                  field="responseDataQuality"
                  infoTooltip="responseDataQualityHint"
                  options={monitoringSPR}
                  disabled={!appliedEvaluationType.includes(3) || !isEditable}
                  blueBg={true}
                  error={
                    !monitoringProposalFieldValidation?.about?.response_quality_id && evaluationType.includes(3)
                      ? {
                          field: 'responseDataQuality',
                          error: 'pleaseCompleteThisField'
                        }
                      : { field: '', error: '' }
                  }
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col md={8} mdOffset={2}>
              <div style={{ position: 'relative' }}>
                <Textarea
                  label="additionalSiteFeedback"
                  value={additionalFeedback}
                  field="additionalSiteFeedback"
                  onChange={onAdditionalFeedbackChange}
                  placeholder="additionalSiteFeedbackPlaceholder"
                  height="128px"
                  dataTest="reviewers-text-area"
                  isOptional={true}
                  disabled={!isEditable}
                  marginBottom="0"
                  maxLength={MAX_TEXTBOX_CHAR_LIMIT}
                  infoTooltip="additionalSiteFeedbackHint"
                  showCharCount={true}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={8} mdOffset={2}>
              <div style={{ position: 'relative', marginTop: '24px' }}>
                <Textarea
                  label="provideAnyReferencesSources"
                  value={provideAnyReferences}
                  field="provideAnyReferencesSources"
                  onChange={onProvideAnyReferencesChange}
                  placeholder="provideAnyReferencesSourcesPlaceholder"
                  height="128px"
                  dataTest="reviewers-text-area"
                  isOptional={true}
                  disabled={!isEditable}
                  marginBottom="0"
                  infoTooltip="provideAnyReferencesSourcesHint"
                  maxLength={MAX_TEXTBOX_CHAR_LIMIT}
                  showCharCount={true}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={8} mdOffset={2}>
              <div style={{ position: 'relative', marginTop: '24px' }}>
                <UploadSiteFiles
                  label="uploadRelevantFiles"
                  field="uploadRelevantFiles"
                  infoTooltip="uploadRelevantFilesHint"
                  height="128px"
                  dataTest="uploadRelevantFiles-text-area"
                  isOptional={true}
                  disabled={!isEditable}
                />
                <Uploader params={mergedParams} siteFiles={monitoringFiles || []} disabled={!isEditable} />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={8} mdOffset={2}>
              <div style={{ position: 'relative', marginTop: '16px' }}>
                <Textarea
                  label="anyOtherDetailsRelevance"
                  value={anyOtherDetailsRelevance}
                  field="anyOtherDetailsRelevance"
                  onChange={onAnyOtherDetailsRelevanceChange}
                  placeholder="anyOtherDetailsRelevancePlaceholder"
                  height="128px"
                  dataTest="reviewers-text-area"
                  isOptional={true}
                  infoTooltip="anyOtherDetailsRelevancePlaceholder"
                  disabled={!isEditable}
                  marginBottom="0"
                  maxLength={MAX_TEXTBOX_CHAR_LIMIT}
                  showCharCount={true}
                />
              </div>
            </Col>
          </Row>
          <ScrollToTop bottom="104px" />
        </Container>
      )}
    </div>
  )
}
export default MonitoringAbout
